import type { TeamTargetResponsePagedData } from '@on3/api';
import { Pen } from '@on3/ui-lib/components/Svg';
import { useAuth } from '@on3/ui-lib/index';
import { databaseUrl } from '@on3/ui-lib/utils/api';
import { userHasPermission } from '@on3/ui-lib/utils/auth/access';
import { profileAdmin } from '@on3/ui-lib/utils/auth/constants';
import { TopInterestsMock } from 'components/Db/Profile/PlayerInterests/TopInterestsModule/Mock';
import useAthlete from 'contexts/AthleteProvider';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { type AthleteType } from 'utils/player';

import { PlayerInterestsHeader } from './PlayerInterestsHeader';
import { PlayerInterestsItem } from './PlayerInterestsItem';
import styles from './PlayerInterestsModule.module.scss';

interface IPlayerInterestsModuleProps extends TeamTargetResponsePagedData {
  recruitmentKey?: number | null;
  playerType?: AthleteType;
}

export const PlayerInterestsModule = ({
  list,
  recruitmentKey,
  playerType,
}: IPlayerInterestsModuleProps) => {
  const router = useRouter();
  const { athlete, hasBaseProfile } = useAthlete();
  const { user, hasRdbAccess } = useAuth();
  const viewAllRoute = `/db/${router.query.id}/recruiting/${recruitmentKey}/interests/`;
  const viewAllRPMRoute = `/db/${router.query.id}/recruiting/${recruitmentKey}/rpm/`;
  const editLink = `${databaseUrl}/people/${router.query.id}/recruiting/${recruitmentKey}/edit/#teamTargets`;

  const canEditProfile =
    userHasPermission(profileAdmin, user) && hasBaseProfile;

  const showMock = hasBaseProfile && !athlete.athleteVerified;

  return (
    <div className={styles.block}>
      {showMock ? (
        <TopInterestsMock interests={list ?? []} />
      ) : (
        <>
          <div role="table">
            <PlayerInterestsHeader
              playerType={playerType}
              positionAbbreviation={list ? list[0].positionAbbreviation : 'pos'}
            />
            <ul>
              {list?.map((team) => (
                <PlayerInterestsItem
                  key={team?.team?.key}
                  playerType={playerType}
                  {...team}
                />
              ))}
            </ul>
          </div>
          {canEditProfile && (
            <div>
              <Link
                className={styles.edit}
                href={
                  canEditProfile
                    ? `/db/${athlete.slug}/elite/network/profile/top-interests/`
                    : `/db/${athlete.slug}/claim-profile/`
                }
              >
                <Pen />
                Edit Teams
              </Link>
            </div>
          )}
        </>
      )}
      {recruitmentKey && (
        <footer className={styles.footer}>
          <Link href={viewAllRoute}>All Teams</Link>
          <Link href={viewAllRPMRoute}>All Predictions</Link>
          {hasRdbAccess() && (
            <Link href={editLink} target="_blank">
              Add Interest
            </Link>
          )}
        </footer>
      )}
    </div>
  );
};
